import type React from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import androidIcon from '@/assets/Footer/android.svg';
import appleIcon from '@/assets/Footer/apple.svg';
import qrCodeImage from '@/assets/ukvg website download QR.png';
import { color } from '@/themes/color';
import { typography } from '@/themes/typography';

type StorePlatform = 'apple' | 'android';

export interface StoreBadgeLinkProps {
  platform?: StorePlatform;
  href?: string;
  ariaLabel?: string;
  showQRCode?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

const storeBadgeSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  borderRadius: '10px',
  backgroundColor: color.cream['100'],
  color: color.charcoal.default,
  px: '16px',
  minHeight: { xs: '48px', sm: '56px' },
  border: `1px solid ${color.grey['60']}`,
  textDecoration: 'none',
  fontFamily: typography.bodyText.fontFamily,
  '&:hover': { backgroundColor: color.moss['100'] },
} as const;

const STORE_BADGE_CONTENT = {
  apple: {
    icon: appleIcon,
    iconWidth: 28,
    iconHeight: 34,
    title: 'App Store',
    ariaLabel: 'Download on the App Store',
    href: 'https://apps.apple.com/us/app/uk-vineyard-guide/id6762269588',
  },
  android: {
    icon: androidIcon,
    iconWidth: 34,
    iconHeight: 34,
    title: 'Google Play',
    ariaLabel: 'Get it on Google Play',
    href: 'https://play.google.com/store/apps/details?id=com.avochoc.uk_vineyard',
  },
} as const;

export default function StoreBadgeLink({
  platform,
  href,
  ariaLabel,
  onClick,
}: StoreBadgeLinkProps): JSX.Element {
  const badge = platform ? STORE_BADGE_CONTENT[platform] : null;
  const destination = href ?? (badge?.href || '');
  const isExternal = destination.startsWith('http');

  // Show QR code on desktop, buttons on mobile
  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      {/* QR Code - Desktop only */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
        }}
      >
        <Box
          component="img"
          src={qrCodeImage}
          alt="Download app QR code"
          sx={{
            width: '123px',
            height: '123px',
            borderRadius: '10px',
            border: `1px solid ${color.grey['60']}`,
          }}
        />
      </Box>

      {/* Store Buttons - Mobile only */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        {platform ? (
          <Link
            component="a"
            href={destination}
            aria-label={ariaLabel ?? badge?.ariaLabel}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            onClick={onClick}
            sx={storeBadgeSx}
            data-track={`download_${platform}`}
          >
            <Box
              component="img"
              src={badge?.icon}
              alt=""
              sx={{
                width: {
                  xs: badge?.iconWidth ? badge.iconWidth * 0.8 : 0,
                  sm: badge?.iconWidth,
                },
                height: {
                  xs: badge?.iconHeight ? badge.iconHeight * 0.8 : 0,
                  sm: badge?.iconHeight,
                },
                flexShrink: 0,
                display: 'block',
              }}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: {
                    xs: 'var(--font-size-12, 12px)',
                    sm: typography.caption.fontSize,
                  },
                  lineHeight: 1.2,
                  fontWeight: 300,
                  color: color.charcoal.default,
                }}
              >
                Get it on
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: {
                    xs: 'var(--font-size-14, 14px)',
                    sm: typography.bodyText.fontSize,
                  },
                  lineHeight: 1.2,
                  fontWeight: 600,
                  color: color.charcoal.default,
                }}
              >
                {badge?.title}
              </Typography>
            </Box>
          </Link>
        ) : null}
      </Box>
    </Box>
  );
}
